/**
 * GazeTargetLikelihood — turns the current gaze target point + the unified
 * TargetRegistry into a likelihood DISTRIBUTION over targets P_i(t) (§5.10),
 * instead of a single binary "isLookingAt" match.
 *
 * Why a distribution: the existing GazeObjectMatcher answers "is the gaze
 * inside this box?" (binary). The patent describes projecting the gaze onto
 * ALL registered targets and getting a probability vector, including an
 * explicit `unknown` bucket. A distribution also gives us an honest
 * uncertainty measure (entropy) — useful for the gray-area / neutral
 * framing where we prefer to say "uncertain" over a false verdict.
 *
 * Pure logic. Additive; does not replace GazeObjectMatcher.
 */

import type { TargetRegistry, TargetType } from './TargetRegistry';

export interface TargetLikelihood {
  targetId: string;
  type: TargetType | 'unknown';
  /** Probability in [0,1]; the full distribution sums to 1. */
  p: number;
}

export interface GazeLikelihoodResult {
  /** Distribution over registry targets + a trailing 'unknown' entry. */
  distribution: TargetLikelihood[];
  /** Highest-probability target (may be the 'unknown' bucket). */
  top: TargetLikelihood;
  /** Shannon entropy in bits — higher = more uncertain / diffuse gaze. */
  entropy: number;
}

export interface GazeLikelihoodOpts {
  /** Softness of the proximity kernel (normalized distance). Larger = flatter. */
  tau?: number;
  /**
   * Pseudo-distance assigned to the 'unknown' bucket. Gaze must be at least
   * this close to a real target to out-score "looking at nothing in particular".
   */
  unknownDist?: number;
  /** Down-weight low-visibility (coasting/occluded) targets. Default true. */
  weightByVisibility?: boolean;
}

/**
 * Compute P_i(t): a softmax over exp(-dist/tau) of the gaze point to each
 * target center, with an `unknown` bucket so an off-target gaze does not get
 * force-assigned to the nearest object.
 */
export function gazeTargetLikelihood(
  gazeTarget: { x: number; y: number },
  registry: TargetRegistry,
  opts: GazeLikelihoodOpts = {},
): GazeLikelihoodResult {
  const tau = opts.tau ?? 0.12;
  const unknownDist = opts.unknownDist ?? 0.25;
  const weightByVis = opts.weightByVisibility ?? true;

  const raw: Array<{ id: string; type: TargetType | 'unknown'; score: number }> = [];

  for (const tgt of registry.targets) {
    const dx = gazeTarget.x - tgt.center.x;
    const dy = gazeTarget.y - tgt.center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    let score = Math.exp(-dist / tau);
    if (weightByVis) score *= Math.max(0.05, tgt.visibility);
    raw.push({ id: tgt.targetId, type: tgt.type, score });
  }

  // 'unknown' bucket: a fixed-distance pseudo-target.
  raw.push({ id: 'unknown', type: 'unknown', score: Math.exp(-unknownDist / tau) });

  const total = raw.reduce((s, r) => s + r.score, 0) || 1;
  const distribution: TargetLikelihood[] = raw.map((r) => ({
    targetId: r.id,
    type: r.type,
    p: r.score / total,
  }));

  let top = distribution[0];
  for (const d of distribution) if (d.p > top.p) top = d;

  let entropy = 0;
  for (const d of distribution) {
    if (d.p > 0) entropy -= d.p * Math.log2(d.p);
  }

  return { distribution, top, entropy };
}
